import React, { useContext, useEffect, useRef, useState } from "react";
import { StyleSheet, View } from "react-native";
import {
  calculateInventoryCap,
  getInventory,
} from "../../backend/items/Inventory";
import { getItemByIdNullable } from "../../backend/items/Items";
import { PlayerContext } from "../../backend/PlayerContext";
import {
  addContextListener,
  clearContextListener,
} from "../../backend/PlayerContextListeners";
import { VisibilityContext } from "../utility/VisibilityContext";
import { ItemDetails } from "./ItemDetails";
import { ItemGrid } from "./ItemGrid";

export default function InventoryItems(props: {
  disableSelling?: boolean;
  showQuickbar?: boolean;
  isFullBleed?: boolean;
  overrideMaxHeight?: number;
}) {
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;
  const [selectedIndex, setSelectedIndex] = useState(null as number | null);
  const inventory = getInventory(playerContext);
  const itemGridElements = inventory.map((itemStack, index) => {
    return {
      itemId: itemStack.itemOccurrence.itemId,
      amount: itemStack.amount,
      params: itemStack.itemOccurrence.params,
      disableTooltip: index === selectedIndex,
    };
  });
  const selectedItemOccurrence =
    selectedIndex != null
      ? inventory[selectedIndex]?.itemOccurrence
      : undefined;
  const emptySlots = calculateInventoryCap(playerContext) - inventory.length;

  useEffect(() => {
    addContextListener(
      "InventoryComponent",
      (newState) => {
        setSelectedIndex(null);
      },
      (state, oldValue) => {
        const inventory = getInventory(state);
        const selectedItemOccurrence =
          selectedIndex != null
            ? inventory[selectedIndex]?.itemOccurrence
            : undefined;
        return JSON.stringify(selectedItemOccurrence);
      },
    );
    return () => clearContextListener("InventoryComponent");
  }, [selectedIndex]);

  const visible = useContext(VisibilityContext);
  useEffect(() => {
    if (!visible) {
      setSelectedIndex(null);
    }
  }, [visible]);

  const itemDetails = selectedIndex != null && selectedItemOccurrence && (
    <ItemDetails
      disableSelling={props.disableSelling}
      showQuickbar={props.showQuickbar}
      itemStack={inventory[selectedIndex]}
    />
  );

  const onPressItem = React.useCallback(
    (item: any, index: number) => {
      setSelectedIndex(index);
    },
    [setSelectedIndex],
  );

  const onDoublePressItem = React.useCallback(
    (item: any, index: number) => {
      playerContextRef.current.apply((state) => {
        if (selectedItemOccurrence?.itemId != null) {
          const item = getItemByIdNullable(selectedItemOccurrence?.itemId);
          const primaryAction = item?.getPrimaryAction(
            state,
            selectedItemOccurrence.params,
          );
          if (primaryAction != null && primaryAction?.isEnabled) {
            const transform = primaryAction.transform;
            return transform != null ? transform(state) : state;
          }
        }
        return state;
      });
    },
    [JSON.stringify(selectedItemOccurrence)],
  );

  return (
    <View>
      <ItemGrid
        items={itemGridElements}
        onPressItem={onPressItem}
        onDoublePressItem={onDoublePressItem}
        dummySlotAmount={emptySlots}
        overrideMaxHeight={props.overrideMaxHeight}
      />
      <View
        style={[
          styles.detailsContainer,
          props.isFullBleed && styles.detailsContainerPadding,
        ]}
      >
        {itemDetails}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  detailsContainer: {
    marginTop: 18,
  },
  detailsContainerPadding: {
    paddingHorizontal: 8,
    paddingBottom: 8,
  },
});
